/**
 * Reads the shipment out of the iframe URL.
 *
 * The embed view is opened *by* D365 for a specific load, so there is no load
 * picker - the load arrives as query parameters. Three ways, in priority order:
 *
 * 1. Full shipment parameters (fromZip, toZip, weight, dims...). The rate is
 *    then genuinely derived from the load D365 passed. This is what X++ should
 *    send once it can read the load's address and dimensions.
 * 2. `loadId=LOAD-004192` matching a seed load, for quick manual testing.
 * 3. `loadId=USMF-000001` alone - a real D365 id with no shipment detail. This
 *    is the common case while integrating: the form knows which load was
 *    picked but is not passing its contents yet. The id is mapped to a demo
 *    lane *deterministically*, so the same load always shows the same options
 *    and two different loads show different ones - which is what makes the
 *    dropdown feel live. The header labels it a demo lane and shows the lane
 *    and weight actually being priced, so nothing is passed off as that load's
 *    real shipment.
 * 4. Nothing at all - the reference lane, with an explanatory note.
 */

import loadBookRaw from '../../data/loads.json';
import type { Load } from '../types';

const book = loadBookRaw as unknown as { asOfDate: string; loads: Load[] };

export type EmbedResolution = 'parameters' | 'seed-load' | 'demo-lane' | 'fallback';

export interface EmbedContext {
  load: Load;
  resolution: EmbedResolution;
  /** The identifier D365 sent, shown in the header even when unmatched. */
  requestedLoadId: string | null;
  theme: 'light' | 'dark' | null;
  /** Hides the one-line load summary strip. */
  bare: boolean;
  /** Suppresses the Book button, leaving selection only. */
  selectOnly: boolean;
}

function num(v: string | null): number | null {
  if (v === null || v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function flag(v: string | null): boolean {
  return v !== null && /^(1|true|yes|y|on)$/i.test(v.trim());
}

/** yyyy-mm-dd, or D365's M/d/yyyy. */
function isoDate(v: string | null): string | null {
  if (!v) return null;
  const t = v.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(t);
  if (m) return `${m[3]}-${(m[1] ?? '').padStart(2, '0')}-${(m[2] ?? '').padStart(2, '0')}`;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function toRateLinxDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${m}/${d}/${y} 12:00:00 AM`;
}

/** Stable index into the load book from an arbitrary id. */
function hashIndex(key: string, modulo: number): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h >>> 0) % modulo;
}

/** True when there is enough to rate a real shipment. */
function hasShipmentParams(q: URLSearchParams): boolean {
  return Boolean(q.get('toZip') && (q.get('weight') ?? q.get('grossWeight')));
}

function buildLoadFromParams(q: URLSearchParams): Load {
  const today = new Date().toISOString().slice(0, 10);
  const shipDate = isoDate(q.get('shipDate')) ?? today;
  const requiredBy = isoDate(q.get('requiredBy') ?? q.get('deliveryDate')) ?? addDays(shipDate, 5);

  const weight = num(q.get('weight') ?? q.get('grossWeight')) ?? 50;
  const length = num(q.get('length')) ?? 24;
  const width = num(q.get('width')) ?? 20;
  const height = num(q.get('height')) ?? 18;

  const loadId = q.get('loadId') ?? q.get('load') ?? 'LOAD';

  return {
    loadId,
    salesId: q.get('salesId') ?? '',
    poNumber: q.get('poNumber') ?? '',
    custAccount: q.get('custAccount') ?? '',
    custName: q.get('customer') ?? q.get('custName') ?? '',
    status: 'Open',
    residential: flag(q.get('residential')),
    freightClass: q.get('class') ?? q.get('freightClass') ?? '50.0',

    shipDate,
    requiredByDate: requiredBy,
    shipDateRateLinx: toRateLinxDate(shipDate),
    requiredByDateRateLinx: toRateLinxDate(requiredBy),
    daysToDeadline: Math.round(
      (Date.parse(`${requiredBy}T00:00:00Z`) - Date.parse(`${shipDate}T00:00:00Z`)) / 86400000,
    ),

    warehouse: {
      inventLocationId: q.get('warehouse') ?? q.get('fromWarehouse') ?? '',
      inventSiteId: q.get('site') ?? '',
      name: q.get('fromName') ?? 'Origin',
      attention: 'Shipping Department',
      address1: q.get('fromStreet') ?? '',
      city: q.get('fromCity') ?? '',
      state: (q.get('fromState') ?? '').toUpperCase(),
      zip: q.get('fromZip') ?? '',
      country: q.get('fromCountry') ?? 'US',
      phone: '',
    },
    shipTo: {
      name: q.get('toName') ?? q.get('customer') ?? 'Destination',
      address1: q.get('toStreet') ?? '',
      city: q.get('toCity') ?? '',
      state: (q.get('toState') ?? '').toUpperCase(),
      zip: q.get('toZip') ?? '',
      country: q.get('toCountry') ?? 'US',
      phone: '',
    },

    package: { length, width, height, weight },
    pallet: {
      length: num(q.get('palletLength')) ?? length,
      width: num(q.get('palletWidth')) ?? width,
      height: num(q.get('palletHeight')) ?? height,
      pallets: num(q.get('pallets')) ?? 1,
      pieces: num(q.get('pieces')) ?? 1,
    },
    lines: [],
    declaredValue: num(q.get('value')) ?? 0,
    currency: q.get('currency') ?? 'USD',
    totalWeight: weight,
    estimatedDimWeight: Math.round(((length * width * height) / 139) * 100) / 100,
  } as Load;
}

/** Embed mode is either the /embed path or ?view=embed on any path. */
export function isEmbedMode(): boolean {
  const { pathname, search, hash } = window.location;
  if (/^\/embed\/?$/i.test(pathname)) return true;
  if (new URLSearchParams(search).get('view') === 'embed') return true;
  return /[?&]view=embed\b/i.test(hash);
}

export function readEmbedContext(): EmbedContext {
  // Support params after the hash too, so #/embed?load=… works if a host
  // rewrites the path away.
  const hashQuery = window.location.hash.includes('?')
    ? window.location.hash.slice(window.location.hash.indexOf('?'))
    : '';
  const q = new URLSearchParams(`${window.location.search}&${hashQuery.replace(/^\?/, '')}`);

  const themeParam = (q.get('theme') ?? '').toLowerCase();
  const theme = themeParam === 'light' || themeParam === 'dark' ? themeParam : null;
  const bare = flag(q.get('bare'));
  const selectOnly = flag(q.get('selectOnly')) || flag(q.get('noBook'));

  const requestedLoadId = q.get('loadId') ?? q.get('load');

  if (hasShipmentParams(q)) {
    return { load: buildLoadFromParams(q), resolution: 'parameters', requestedLoadId, theme, bare, selectOnly };
  }

  if (requestedLoadId) {
    const seed = book.loads.find((l) => l.loadId.toLowerCase() === requestedLoadId.toLowerCase());
    if (seed) {
      return { load: seed, resolution: 'seed-load', requestedLoadId, theme, bare, selectOnly };
    }

    // A real D365 id with no shipment detail. Map it to a demo lane, stably, so
    // the dropdown drives the options instead of every load looking identical.
    const mapped = book.loads[hashIndex(requestedLoadId.toUpperCase(), book.loads.length)] as Load;
    return { load: mapped, resolution: 'demo-lane', requestedLoadId, theme, bare, selectOnly };
  }

  const fallback = book.loads[0] as Load;
  return { load: fallback, resolution: 'fallback', requestedLoadId, theme, bare, selectOnly };
}
